import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  editStudyRequest,
  FetchStudiesListQueryParam,
  FetchStudiesResponse,
  newStudyRequest,
  NewStudyResponse,
  PreviousStudyResponse,
  StudiesDataResponse,
  StudiesListQueryParam,
  StudiesListResponse,
  Study,
  studyResponse,
  StudyTypeRespose
} from '@shared/models';
import { buildHttpParamsFromQueryParams } from '@shared/utils';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class StudiesService {
  private currentStudy: studyResponse | unknown;
  private currentStudySubject = new BehaviorSubject<StudiesDataResponse | unknown>([]);
  currentStudy$ = this.currentStudySubject.asObservable();
  private _apiUrl = environment.apiUrl;
  private _fakeUrl = 'assets/mock-data/studies-data.json'
  private _http = inject(HttpClient);
  private _authHeaders = new HttpHeaders({
    Authorization: '',
    "Accept": "application/json"
  });

  public getStudiesList(
    queryParams?: Partial<StudiesListQueryParam>
  ): Observable<FetchStudiesResponse> {
    const queryParamsMapped: Partial<FetchStudiesListQueryParam> = {
      id: queryParams?.id,
      study_date: queryParams?.date,
      studyTypeName: queryParams?.studyTypeName,
      InstitutionName: queryParams?.institutionName,
      medicName: queryParams?.medicName,
      patient: queryParams?.pacient,
      order_by: queryParams?.order_by,
      order_direction: queryParams?.order_direction,
      page: queryParams?.page,
      per_page: queryParams?.per_page,
    };
    const params: HttpParams | undefined = buildHttpParamsFromQueryParams(queryParamsMapped);
    const url: string = `${this._apiUrl}/api/study/list`;

    return this._http.get<FetchStudiesResponse>(
      url,
      {
        headers: this._authHeaders,
        params,
      }
    )
  }


  //TODO: change the name to a more referential one, it refers to the list of studies available to make
  public getStudies(
    queryParams?: Partial<Study>
  ): Observable<StudiesListResponse> {
    const queryParamsMapped: Partial<Study> = {
      id: queryParams?.id,
      name: queryParams?.name,
    };
    const params = buildHttpParamsFromQueryParams(queryParamsMapped);
    return this._http.get<StudiesListResponse>(
      'assets/mock-data/studies.json',
      {
        headers: this._authHeaders,
        params,
      }
    );
  }

  fetchPreviousStudies(patientId: number): Observable<PreviousStudyResponse> {
    return this._http.get<PreviousStudyResponse>(`${this._apiUrl}/api/patient/${patientId}/medical-record`, {
      headers: this._authHeaders
    });
  }

  public createNewStudy(payload: newStudyRequest): any {
    const url: string = `${this._apiUrl}/api/study`;
    const formData: FormData = this.buildFormData(payload);

    return this._http.post<NewStudyResponse>(url, formData, {
        headers: this._authHeaders,
    });
  }

  public editPreviousStudy(payload: editStudyRequest): Observable<any> {
    const formData = this.buildEditFormData(payload); 
    return this._http.post(('assets/mock-data/studies-data.json'), formData, {
      headers: this._authHeaders,
    });
  }
 
  getStudyById(id: number): Observable<studyResponse | unknown > {
    return this._http.get<{ message: string, data: StudiesDataResponse[] }>(this._fakeUrl).pipe(
      map(response => {
        if (Array.isArray(response.data)) {
          return response.data.find(study => study.id == id) || null;
        } else {
          console.error('error datos vacion:', response.data);
          return null;
        }
      }),
      tap(study => {
        if (study) {
          this.setCurrentStudy(study);
        } else {
          console.warn(`no se encontro el id ${id}`);
        }
      })
    );
  }

  setCurrentStudy(study: studyResponse): void {
    this.currentStudy = study;
    this.currentStudySubject.next(study);
  }
  getCurrentStudy(): studyResponse | unknown {
    return this.currentStudy;
  }

  clearCurrentStudy(): void {
    this.currentStudy = null;
  }

  
  private buildFormData(payload: newStudyRequest): FormData {
    const formData = new FormData();

    formData.append('study_type_id', JSON.stringify(payload.study_type_id));
    formData.append('patient_id', JSON.stringify(payload.patient_id));
    formData.append('date_study', payload.date_study);
    formData.append('medic_id', JSON.stringify(payload.medic_id));
    formData.append('institution_id', JSON.stringify(payload.institution_id));
    formData.append('scanner_id', JSON.stringify(payload.scanner_id));
    formData.append('selected_color', JSON.stringify(payload.selected_color));
    formData.append('stackedCrossSections', JSON.stringify(payload.stackedCrossSections));
    formData.append('measures', JSON.stringify(payload.measures));

    payload['3D_image'].forEach((file: File, index: number) => {
      formData.append(`3D_image[${index}]`, file, file.name);
    });

    payload['individual_cross_sections'].forEach((file: File) => {
      formData.append(`individual_cross_sections`, file, file.name);
    });

    payload['previous_studies']?.forEach((studyId: number, index: number) => {
      formData.append(`previous_studies[${index}]`, studyId.toString()); 
    });

    return formData; 
  }

  private buildEditFormData(payload: editStudyRequest): FormData {
    const formData = new FormData();
    return formData; 
  }

  public getStudyTypes(): Observable<StudyTypeRespose> {
    const url: string = `${this._apiUrl}/api/study-type/list`;

    return this._http.get<StudyTypeRespose>(
      url,
      {
        headers: this._authHeaders,
      }
    );
  }

}
